import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, LOGO_URL } from "@/lib/api";
import { CATEGORIES, LEAD_STATUSES, FOLLOWUP_STATUSES } from "@/lib/constants";
import {
  Download, LogOut, Search, RefreshCw, Users, Eye, BarChart3,
  ChevronRight, Filter, X, Save, FileText,
} from "lucide-react";

function Stat({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="stat-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase font-bold tracking-wider text-slate-500">{label}</p>
          <p className="display text-3xl font-extrabold text-[#0C1F4A] mt-1">{value}</p>
          {sub && <p className="text-[12px] text-slate-500 mt-1">{sub}</p>}
        </div>
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: color + "1A", color }}>
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

function Drawer({ open, item, onClose, onSave, onDownloadPdf }) {
  const [lead, setLead] = useState(item?.lead_status || "General Lead");
  const [followup, setFollowup] = useState(item?.followup_status || "Not Contacted");
  const [officer, setOfficer] = useState(item?.follow_up_officer || "");
  const [notes, setNotes] = useState(item?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setLead(item.lead_status || "General Lead");
      setFollowup(item.followup_status || "Not Contacted");
      setOfficer(item.follow_up_officer || "");
      setNotes(item.notes || "");
    }
  }, [item]);

  if (!open || !item) return null;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(item.id, { lead_status: lead, followup_status: followup, follow_up_officer: officer, notes });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="submission-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#E96C0F] font-bold">Submission</p>
            <h3 className="display text-lg font-extrabold text-[#0C1F4A]">{item.full_name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="drawer-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          <section className="grid grid-cols-2 gap-3">
            <div><p className="text-[11px] text-slate-500 uppercase">Organisation</p><p className="font-semibold text-[#0C1F4A]">{item.organisation}</p></div>
            <div><p className="text-[11px] text-slate-500 uppercase">Position</p><p className="font-semibold text-[#0C1F4A]">{item.position}</p></div>
            <div><p className="text-[11px] text-slate-500 uppercase">Email</p><p className="text-[#0C1F4A] break-all">{item.email}</p></div>
            <div><p className="text-[11px] text-slate-500 uppercase">Phone</p><p className="text-[#0C1F4A]">{item.phone}</p></div>
            <div><p className="text-[11px] text-slate-500 uppercase">Country</p><p className="text-[#0C1F4A]">{item.country}</p></div>
            <div><p className="text-[11px] text-slate-500 uppercase">State/City</p><p className="text-[#0C1F4A]">{item.state_city}</p></div>
            <div className="col-span-2"><p className="text-[11px] text-slate-500 uppercase">Website / Social</p><p className="text-[#0C1F4A] break-all">{item.website || "—"}</p></div>
            <div className="col-span-2"><p className="text-[11px] text-slate-500 uppercase">Main Interest</p><p className="text-[#0C1F4A]">{item.main_interest}</p></div>
            <div className="col-span-2"><p className="text-[11px] text-slate-500 uppercase">Category</p><p className="text-[#0C1F4A] font-semibold">{item.category_label}</p></div>
            <div className="col-span-2"><p className="text-[11px] text-slate-500 uppercase">Source</p><p className="text-[#0C1F4A]">{item.source}</p></div>
            <div className="col-span-2"><p className="text-[11px] text-slate-500 uppercase">Submitted</p><p className="text-[#0C1F4A]">{new Date(item.created_at).toLocaleString()}</p></div>
          </section>

          {item.category_data && Object.keys(item.category_data).length > 0 && (
            <section>
              <p className="text-[11px] uppercase font-bold text-[#E96C0F] tracking-wider mb-2">Category responses</p>
              <div className="space-y-2 bg-slate-50 rounded-lg p-3">
                {Object.entries(item.category_data).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[11px] uppercase text-slate-500">{k.replace(/_/g, " ")}</p>
                    <p className="text-[13px] text-[#0C1F4A]">{Array.isArray(v) ? v.join(", ") : String(v || "—")}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="border-t pt-4 space-y-3">
            <p className="text-[11px] uppercase font-bold text-[#E96C0F] tracking-wider">Manage</p>
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <span className="field-label">Lead status</span>
                <select data-testid="drawer-lead-status" className="input-base" value={lead} onChange={(e) => setLead(e.target.value)}>
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="field-label">Follow-up status</span>
                <select data-testid="drawer-followup-status" className="input-base" value={followup} onChange={(e) => setFollowup(e.target.value)}>
                  {FOLLOWUP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="field-label">Follow-up officer</span>
                <input data-testid="drawer-officer" className="input-base" value={officer} onChange={(e) => setOfficer(e.target.value)} placeholder="Name of officer assigned" />
              </label>
              <label className="block">
                <span className="field-label">Internal notes</span>
                <textarea data-testid="drawer-notes" className="input-base" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
              <button onClick={save} disabled={saving} data-testid="drawer-save-btn"
                className="btn-orange inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold disabled:opacity-70">
                <Save size={16} /> {saving ? "Saving..." : "Save changes"}
              </button>
              <button type="button" onClick={() => onDownloadPdf?.(item.id)} data-testid="drawer-download-pdf-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold border border-slate-200 text-[#0C1F4A] hover:bg-slate-50">
                <FileText size={16} /> Download personalised &quot;Next Steps&quot; PDF
              </button>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [leadStatus, setLeadStatus] = useState("all");
  const [followupStatus, setFollowupStatus] = useState("all");
  const [active, setActive] = useState(null);

  const adminEmail = localStorage.getItem("atff_admin_email");

  const logout = () => {
    localStorage.removeItem("atff_admin_token");
    localStorage.removeItem("atff_admin_email");
    navigate("/admin/login");
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions", {
          params: {
            q: q || undefined,
            category: category !== "all" ? category : undefined,
            lead_status: leadStatus !== "all" ? leadStatus : undefined,
            followup_status: followupStatus !== "all" ? followupStatus : undefined,
          },
        }),
      ]);
      setStats(s.data); setItems(l.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [category, leadStatus, followupStatus]);

  const exportCsv = async () => {
    const token = localStorage.getItem("atff_admin_token");
    const url = `${API}/admin/export${category !== "all" ? `?category=${category}` : ""}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `atff_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const updateSub = async (id, body) => {
    const { data } = await api.patch(`/admin/submissions/${id}`, body);
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...data } : x)));
    setActive((a) => (a && a.id === id ? { ...a, ...data } : a));
  };

  const downloadPdf = async (id) => {
    const token = localStorage.getItem("atff_admin_token");
    const res = await fetch(`${API}/admin/submissions/${id}/next-steps-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ATFF_Next_Steps_${id.slice(0, 8)}.pdf`;
    a.click();
  };

  const catCounts = useMemo(() => {
    const m = {};
    (stats?.category_breakdown || []).forEach((c) => { m[c.key] = c.count; });
    return m;
  }, [stats]);

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ATFF" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <p className="display font-bold text-[#0C1F4A] text-sm">ATFF Admin · Waitlist</p>
              <p className="text-[11px] text-slate-500">ADTF 2026 · Lagos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[12px] text-slate-500">{adminEmail}</span>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0C1F4A] px-3 py-1.5 rounded-lg hover:bg-slate-100">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-7 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Submissions" value={stats?.total_submissions ?? "—"} icon={Users} color="#F58220" />
          <Stat label="Page views" value={stats?.total_views ?? "—"} icon={Eye} color="#0C1F4A" />
          <Stat label="Conversion" value={`${stats?.conversion_rate ?? 0}%`} sub="views → submissions" icon={BarChart3} color="#2F9E44" />
          <Stat label="Categories filled" value={(stats?.category_breakdown || []).length} icon={Filter} color="#E96C0F" />
        </div>

        <section className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 stat-card">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">By category</p>
            <div className="space-y-2">
              {CATEGORIES.map((c) => {
                const count = catCounts[c.key] || 0;
                const max = Math.max(1, ...Object.values(catCounts));
                const pct = (count / max) * 100;
                return (
                  <div key={c.key} className="flex items-center gap-3">
                    <span className="text-[13px] text-[#0C1F4A] w-1/3 truncate">{c.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#F58220,#2F9E44)" }} />
                    </div>
                    <span className="text-[12px] font-bold text-[#0C1F4A] w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="stat-card">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">Top countries</p>
            <ul className="space-y-2">
              {(stats?.country_breakdown || []).slice(0, 8).map((r) => (
                <li key={r.country} className="flex items-center justify-between text-[13px]">
                  <span className="text-[#0C1F4A]">{r.country}</span>
                  <span className="tag tag-blue">{r.count}</span>
                </li>
              ))}
              {(stats?.country_breakdown || []).length === 0 && (
                <li className="text-[12px] text-slate-500">No data yet.</li>
              )}
            </ul>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200">
          <div className="p-4 sm:p-5 border-b flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
              <input
                data-testid="search-input" className="input-base pl-9"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadAll()}
                placeholder="Search by name, organisation, country, email, phone..."
              />
            </div>
            <select data-testid="filter-category" className="input-base w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select data-testid="filter-lead-status" className="input-base w-auto" value={leadStatus} onChange={(e) => setLeadStatus(e.target.value)}>
              <option value="all">All lead statuses</option>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="filter-followup-status" className="input-base w-auto" value={followupStatus} onChange={(e) => setFollowupStatus(e.target.value)}>
              <option value="all">All follow-up</option>
              {FOLLOWUP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={loadAll} data-testid="refresh-btn"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0C1F4A] px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={exportCsv} data-testid="export-csv-btn"
              className="btn-orange inline-flex items-center gap-1.5 text-[13px] font-bold px-4 py-2 rounded-full">
              <Download size={14} /> Export CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Organisation</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Lead</th>
                  <th className="text-left px-4 py-3">Follow-up</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-testid="empty-state">
                    No submissions match your filters yet.
                  </td></tr>
                )}
                {!loading && items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                      data-testid={`row-${it.id}`} onClick={() => setActive(it)}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#0C1F4A]">{it.full_name}</p>
                      <p className="text-[11px] text-slate-500">{it.email}</p>
                    </td>
                    <td className="px-4 py-3 text-[#0C1F4A]">{it.organisation}</td>
                    <td className="px-4 py-3"><span className="tag">{it.category_label}</span></td>
                    <td className="px-4 py-3 text-[#0C1F4A]">{it.country}</td>
                    <td className="px-4 py-3"><span className="tag tag-blue">{it.lead_status}</span></td>
                    <td className="px-4 py-3"><span className="tag tag-gray">{it.followup_status}</span></td>
                    <td className="px-4 py-3 text-slate-600">{new Date(it.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right text-slate-400"><ChevronRight size={16} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <Drawer open={!!active} item={active} onClose={() => setActive(null)} onSave={updateSub} onDownloadPdf={downloadPdf} />
    </div>
  );
}
